/** 查词页: 中英文按需分页检索, 详情复用共享词卡弹层. */

import { ApiError, api } from '../lib/api'
import { t } from '../lib/i18n'
import type { WordLookupItem } from '../types/api'

const PAGE_SIZE = 20
const HISTORY_LIMIT = 8

/** 搜索和翻页使用同一版本号, 旧响应不能覆盖新的查询. */
export function dictionaryPage() {
  let version = 0
  let userId: number | null = null
  return {
    recent: [] as string[],
    draft: '',
    query: '',
    words: [] as WordLookupItem[],
    total: 0,
    searched: false,
    loading: false,
    loadingMore: false,
    error: '',

    /** 账号切换时清理页面与旧请求, 避免共用设备串入其他人的查询. */
    syncUser(id: number | null): void {
      if (id === userId) return
      userId = id
      this.clear()
      this.recent = []
      if (id === null) return
      try {
        const stored: unknown = JSON.parse(localStorage.getItem(`oma-gendu:dictionary-history:${id}`) ?? '[]')
        if (Array.isArray(stored)) {
          this.recent = [...new Set(stored.filter((item): item is string =>
            typeof item === 'string' && item.trim().length > 0 && item.length <= 80,
          ))].slice(0, HISTORY_LIMIT)
        }
      } catch {
        // 存储不可用或旧数据损坏时, 查词仍可正常使用.
      }
    },

    /** 历史只存查询词, 不缓存词库或词卡内容. */
    saveRecent(): void {
      if (userId === null) return
      try {
        const key = `oma-gendu:dictionary-history:${userId}`
        if (this.recent.length) localStorage.setItem(key, JSON.stringify(this.recent))
        else localStorage.removeItem(key)
      } catch {
        // 隐私模式或存储已满时保留本次会话的历史.
      }
    },

    /** 只记录成功返回的查询, 大小写不同的英文查询合并. */
    remember(): void {
      this.recent = [this.query, ...this.recent.filter((item) =>
        item.toLocaleLowerCase() !== this.query.toLocaleLowerCase(),
      )].slice(0, HISTORY_LIMIT)
      this.saveRecent()
    },

    /** 点击历史与输入提交共用请求和过期响应保护. */
    searchRecent(query: string): void {
      this.draft = query
      void this.search()
    },

    /** 清除当前账号在此设备的查询记录. */
    clearRecent(): void {
      this.recent = []
      this.saveRecent()
    },

    /** 空关键词回到初始状态, 不请求整个词库. */
    async search(): Promise<void> {
      const current = ++version
      this.query = this.draft.trim()
      this.error = ''
      this.loadingMore = false
      if (!this.query) {
        this.words = []
        this.total = 0
        this.searched = false
        this.loading = false
        return
      }
      this.loading = true
      try {
        const page = await api.words.search(this.query, PAGE_SIZE)
        if (current !== version) return
        this.words = page.items
        this.total = page.meta.total
        this.searched = true
        this.remember()
      } catch (error) {
        if (current === version) this.error = error instanceof ApiError ? error.message : t('dictionary.error')
      } finally {
        if (current === version) this.loading = false
      }
    },

    /** 只在用户点击时加载下一页, 不做整库预取. */
    async loadMore(): Promise<void> {
      if (this.loading || this.loadingMore || this.words.length >= this.total) return
      const current = version
      this.loadingMore = true
      this.error = ''
      try {
        const page = await api.words.search(this.query, PAGE_SIZE, this.words.length)
        if (current !== version) return
        this.words = [...this.words, ...page.items]
        this.total = page.meta.total
      } catch (error) {
        if (current === version) this.error = error instanceof ApiError ? error.message : t('dictionary.error')
      } finally {
        if (current === version) this.loadingMore = false
      }
    },

    clear(): void {
      this.draft = ''
      void this.search()
    },

  }
}
